import { useMemo, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, ChevronDown } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { CTASection } from "@/components/site/CTASection";
import { PageHeroCard } from "@/components/site/PageHeroCard";
import { PageMeta } from "@/components/site/PageMeta";
import { Button } from "@/components/ui/button";
import { useLang } from "@/i18n/LangContext";
import { certificates } from "@/data/certificates";
import { findCertificateBySlug, certificatePathFor } from "@/data/certificateSlugs";
import { getBookingAction, isContactAction } from "@/data/certificateBookingActions";
import { BookingDialog } from "@/components/site/BookingDialog";
import { CategoryBadge } from "@/components/site/CategoryBadge";
import { ModeBadges } from "@/components/site/ModeBadge";
import { SamplingLocationsSection } from "@/components/site/SamplingLocationsSection";
import { getCertDetails } from "@/data/certificateDetails";
import { cn } from "@/lib/utils";
import hogreBehorighetImg from "@/assets/certificates/hogre-behorighet.jpg";
import alkolasImg from "@/assets/certificates/alkolas.jpg";
import drogerOchAlkoholImg from "@/assets/certificates/droger-och-alkohol.jpg";
import flygplanImg from "@/assets/certificates/flygplan.jpg";
import baggageImg from "@/assets/certificates/baggage.jpg";
import pregnantFlyingImg from "@/assets/certificates/pregnant-flying.jpg";
import adoptionImg from "@/assets/certificates/adoption.avif";
import offshoreImg from "@/assets/certificates/offshore.jpg";
import sjofolkImg from "@/assets/certificates/sjofolk.jpg";
import forsakringImg from "@/assets/certificates/forsakring.jpg";
import afsImg from "@/assets/certificates/AFS.jpg";
import auPairImg from "@/assets/certificates/au-pair.jpg";
import sickImg from "@/assets/certificates/sick.jpg";
import utlandsstudierImg from "@/assets/certificates/utlandsstudier.jpg";
import nattarbeteImg from "@/assets/certificates/nattarbete.jpg";
import fallskarmImg from "@/assets/certificates/fallskarm.jpg";
import sportImg from "@/assets/certificates/sport.jpg";
import ovrigaImg from "@/assets/certificates/ovriga.jpg";

const certificateHeroImages: Record<string, string> = {
  "hogre-behorighet": hogreBehorighetImg,
  "c-ce": hogreBehorighetImg,
  "d-de": hogreBehorighetImg,
  "buss-lastbil": hogreBehorighetImg,
  "taxi": hogreBehorighetImg,
  "alkolas": alkolasImg,
  "droger-alkohol": drogerOchAlkoholImg,
  salivtest: drogerOchAlkoholImg,
  urinprov: drogerOchAlkoholImg,
  "korkort-adhd": hogreBehorighetImg,
  "fit-for-fly": flygplanImg,
  "installd-resa": baggageImg,
  "reseintyg-gravid": pregnantFlyingImg,
  adoptionsintyg: adoptionImg,
  offshore: offshoreImg,
  oguk: offshoreImg,
  sjofolksintyg: sjofolkImg,
  "intyg-forsakring": forsakringImg,
  invaliditetsintyg: forsakringImg,
  "afs-allergi-kemiska": afsImg,
  "afs-asbest": afsImg,
  "afs-bly": afsImg,
  "afs-handintensivt": afsImg,
  "au-pair": auPairImg,
  forstadagsintyg: sickImg,
  utlandsstudier: utlandsstudierImg,
  nattarbete: nattarbeteImg,
  fallskarmsintyg: fallskarmImg,
  tavlingsintyg: sportImg,
  ovriga: ovrigaImg,
};

const CertificateDetail = () => {
  const { lang, t } = useLang();
  const { slug } = useParams<{ slug: string }>();
  const internal = slug ? findCertificateBySlug(slug, lang) : null;
  const cert = useMemo(() => certificates.find((c) => c.slug === internal) ?? null, [internal]);

  const [bookDialogOpen, setBookDialogOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const details = useMemo(() => cert ? getCertDetails(cert.slug) : null, [cert]);

  if (!cert) {
    return <Navigate to={lang === "sv" ? "/lakarintyg" : "/en/lakarintyg"} replace />;
  }

  const pageTitle = `${cert.title[lang]} | Intygsläkaren`;

  const selectedAction = getBookingAction(cert.slug);
  const usesContactForm = isContactAction(selectedAction);

  const related = certificates.filter((c) => c.category === cert.category && c.slug !== cert.slug).slice(0, 3);
  const heroImage = certificateHeroImages[cert.slug];
  const heroImagePosition = cert.slug === "installd-resa" ? "center 30%" : "center";

  const labels = lang === "sv"
    ? {
        back: "Tillbaka till alla läkarintyg",
        bookCta: usesContactForm ? "Kontakta oss" : "Boka tid",
        howTitle: "Så här fungerar det",
        howSubtitle: "En enkel och trygg process för att få ditt läkarintyg:",
        steps: details?.steps?.sv ?? [
          "Välj fysiskt eller online besök och boka en tid som passar dig.",
          "Specialistläkaren går igenom kraven och slutför undersökningen.",
          "Efter godkänd undersökning utfärdas ditt intyg.",
        ],
        includedTitle: "Det här ingår",
        bring: "Förbered ditt ärende",
        bringSubtitle: "Ha det här redo inför ditt besök:",
        bringItems: details?.preparation?.sv ?? [
          "Giltig legitimation",
          "Eventuell aktuell medicinering",
          "Underlag eller blanketter från arbetsgivare/myndighet",
        ],
        priceLabel: "Pris",
        faqTitle: "Vanliga frågor",
        faqGeneric: [
          {
            q: "Kan jag göra besöket online?",
            a: "Det beror på intygets krav. Många intyg kan utfärdas efter ett videobesök när reglerna tillåter det. Välj besöksform i bokningen så syns de tillgängliga alternativen.",
          },
          {
            q: "Hur snabbt kan jag få en tid?",
            a: "Vi har ofta lediga tider inom några dagar. Välj en tid direkt i onlinebokningen – inga remisser eller mellanhänder behövs.",
          },
          {
            q: "Vad händer om intyget inte kan utfärdas direkt?",
            a: "Läkaren informerar dig vad som behöver kompletteras – till exempel ytterligare underlag, provtagning eller en återkoppling från din behandlande läkare. Du får tydlig information om nästa steg.",
          },
          {
            q: "Hur lång tid tar besöket?",
            a: "De flesta besök tar 15–30 minuter. Intyg som kräver undersökning eller provtagning kan ta lite längre. Du får mer information när du bokar.",
          },
        ],
        relatedTitle: "Relaterade läkarintyg",
      }
    : {
        back: "Back to all medical certificates",
        bookCta: usesContactForm ? "Contact us" : "Book appointment",
        howTitle: "How it works",
        howSubtitle: "A simple and secure process to receive your medical certificate:",
        steps: details?.steps?.en ?? [
          "Choose between an on-site or online visit and book a time that suits you.",
          "The specialist doctor reviews the requirements and completes the examination.",
          "After an approved examination your certificate is issued.",
        ],
        includedTitle: "What's included",
        bring: "Prepare your case",
        bringSubtitle: "Have the following ready for your appointment:",
        bringItems: details?.preparation?.en ?? [
          "Valid ID",
          "Any current medications",
          "Forms or documents from your employer/authority",
        ],
        priceLabel: "Price",
        faqTitle: "Frequently asked questions",
        faqGeneric: [
          {
            q: "Can I have the appointment online?",
            a: "It depends on the certificate's requirements. Many certificates can be issued after a video consultation when the rules allow it. The available options are shown when you book.",
          },
          {
            q: "How quickly can I get an appointment?",
            a: "We usually have available slots within a few days. Book directly online — no referrals or intermediaries needed.",
          },
          {
            q: "What happens if the certificate cannot be issued straight away?",
            a: "The doctor will tell you what needs to be completed — for example additional documentation, testing, or feedback from your treating doctor. You will receive clear information about the next steps.",
          },
          {
            q: "How long does the appointment take?",
            a: "Most appointments take 15–30 minutes. Certificates requiring an examination or testing may take a little longer. You will receive more information when you book.",
          },
        ],
        relatedTitle: "Related certificates",
      };

  return (
    <div className="min-h-screen bg-background">
      <PageMeta title={pageTitle} description={cert.description[lang]} />
      <Header />
      <main>
        <PageHeroCard
          imageSrc={heroImage}
          imageAlt={cert.title[lang]}
          imageObjectPosition={heroImagePosition}
          compact
        >
          <div className="relative z-20 grid items-end gap-8 lg:grid-cols-[1fr_minmax(20rem,28rem)] lg:gap-12">
            <div className="animate-fade-up max-w-2xl">
              <CategoryBadge category={cert.category} tone="white" size="md" link />
              <h1 className="mt-4 text-balance font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
                {cert.title[lang]}
              </h1>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
                {cert.description[lang]}
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
              </div>
            </div>

            <div className="relative z-20 flex w-full flex-col justify-between rounded-2xl border border-border/60 bg-white p-6 shadow-card sm:min-h-[13.5rem] sm:p-8 sm:rounded-3xl">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{labels.priceLabel}</span>
                  <div className="mt-1 font-display text-3xl font-semibold text-foreground">{cert.price[lang]}</div>
                </div>
                <ModeBadges modes={cert.modes} size="md" className="justify-start gap-2" />
              </div>
              <Button
                type="button"
                variant="hero"
                size="xl"
                className="mt-8 w-full"
                onClick={() => setBookDialogOpen(true)}
              >
                {labels.bookCta}
              </Button>
            </div>
          </div>
        </PageHeroCard>

        {/* Assessment / about this certificate – shown when there's a lead paragraph */}
        {details?.lead && (
          <section className="pt-12 pb-4 lg:pt-16 lg:pb-6">
            <div className="container-wide">
              <div className="mx-auto max-w-3xl">
                <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
                  {details.lead[lang]}
                </p>
              </div>
            </div>
          </section>
        )}

        {/* Grouped pricing (e.g. droger & alkohol) */}
        {details?.pricing && (
          <section className={`${details?.lead ? "py-10 lg:py-14" : "py-12 lg:py-16"}`}>
            <div className="container-wide">
              <div className="mx-auto max-w-4xl">
                <div className="mx-auto max-w-3xl text-center">
                  <h2 className="font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                    {details.pricing.title[lang]}
                  </h2>
                  {details.pricing.subtitle && (
                    <p className="mt-3 text-muted-foreground">{details.pricing.subtitle[lang]}</p>
                  )}
                </div>

                <div className="mt-10 space-y-10">
                  {details.pricing.groups.map((group) => (
                    <div key={group.title[lang]}>
                      <h3 className="font-display text-xl font-semibold text-foreground sm:text-2xl">
                        {group.title[lang]}
                      </h3>
                      <div
                        className={cn(
                          "mt-5 grid gap-4",
                          group.items.length === 1 ? "grid-cols-1" : "sm:grid-cols-2",
                        )}
                      >
                        {group.items.map((item) => (
                          <div
                            key={item.name[lang]}
                            className={cn(
                              "flex flex-col rounded-2xl border p-6 shadow-card sm:rounded-3xl",
                              item.showBookingCta
                                ? "border-primary/30 bg-primary/5"
                                : "border-border/60 bg-white",
                            )}
                          >
                            <div className="flex flex-wrap items-start justify-between gap-3">
                              <h4 className="font-display text-base font-semibold text-foreground">
                                {item.name[lang]}
                              </h4>
                              <div className="text-right">
                                <div className="font-display text-2xl font-bold text-primary">
                                  {item.price[lang]}
                                </div>
                                {item.unit && (
                                  <div className="mt-0.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                                    {item.unit[lang]}
                                  </div>
                                )}
                              </div>
                            </div>
                            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                              {item.note[lang]}
                            </p>
                            {item.includes?.[lang] && (
                              <ul className="mt-4 space-y-2">
                                {item.includes[lang].map((line) => (
                                  <li
                                    key={line}
                                    className="flex items-start gap-2 text-sm text-muted-foreground"
                                  >
                                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                                    <span>{line}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                            {item.showBookingCta && (
                              <Button
                                type="button"
                                variant="hero"
                                size="lg"
                                className="mt-6 w-full sm:w-auto"
                                onClick={() => setBookDialogOpen(true)}
                              >
                                {lang === "sv" ? "Boka inskrivning" : "Book registration"}
                                <ArrowRight className="ml-2 h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {details.pricing.summary && (
                  <p className="mt-10 text-sm leading-relaxed text-muted-foreground sm:text-base">
                    {details.pricing.summary[lang]}
                  </p>
                )}
              </div>
            </div>
          </section>
        )}

        {/* How it works */}
        <section
          id="sa-har-fungerar-det"
          className={cn(
            details?.lead || details?.pricing ? "py-10 lg:py-14" : "py-12 lg:py-16",
            details?.pricing && "bg-muted/20",
          )}
        >
          <div className="container-wide">
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                {details?.process?.title[lang] ?? labels.howTitle}
              </h2>
              <p className="mt-3 text-muted-foreground">
                {details?.process?.subtitle?.[lang] ?? labels.howSubtitle}
              </p>
            </div>
            {details?.process ? (
              <div className="mx-auto mt-10 grid max-w-4xl gap-4">
                {details.process.items.map((step, i) => (
                  <div
                    key={step.title[lang]}
                    className="flex gap-4 rounded-2xl border border-border/60 bg-white p-5 shadow-card sm:gap-5 sm:rounded-3xl sm:p-6"
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                      {i + 1}
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-display text-base font-semibold text-foreground sm:text-lg">
                        {step.title[lang]}
                      </h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                        {step.body[lang]}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-10 grid gap-6 md:grid-cols-3">
                {labels.steps.map((step, i) => (
                  <div key={i} className="rounded-2xl border border-border/60 bg-white p-6 shadow-card sm:rounded-3xl">
                    <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                      {i + 1}
                    </div>
                    <p className="text-sm text-muted-foreground">{step}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* What's included + What to bring */}
        <section className="pb-12 lg:pb-16">
          <div className="container-wide grid gap-6 md:grid-cols-2">
            {/* What's included */}
            {details?.bullets?.[lang] && (
              <div className="rounded-2xl border border-border/60 bg-white p-8 shadow-card sm:rounded-3xl">
                <h2 className="font-display text-2xl font-semibold text-foreground sm:text-3xl">{labels.includedTitle}</h2>
                <ul className="mt-5 space-y-3">
                  {details.bullets[lang].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-muted-foreground">
                      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* What to bring */}
            <div className={`rounded-2xl border border-border/60 bg-white p-8 shadow-card sm:rounded-3xl ${!details?.bullets?.[lang] ? "md:col-span-2 md:max-w-3xl" : ""}`}>
              <h2 className="font-display text-2xl font-semibold text-foreground sm:text-3xl">{labels.bring}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{labels.bringSubtitle}</p>
              <ul className="mt-5 space-y-3">
                {labels.bringItems.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-muted-foreground">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Detailed pricing for complex services (e.g. alkolås) */}
        {details?.prices && (
          <section className="pb-12 lg:pb-16">
            <div className="container-wide">
              <div className="mx-auto max-w-4xl">
                <h2 className="font-display text-2xl font-semibold text-foreground sm:text-3xl">
                  {lang === "sv" ? "Pris och vad som ingår" : "Pricing and what's included"}
                </h2>
                <div className="mt-6 grid gap-4 sm:grid-cols-3">
                  {details.prices.map((p, i) => (
                    <div
                      key={i}
                      className={`rounded-2xl border p-6 shadow-card sm:rounded-3xl ${i === 0 ? "border-primary/30 bg-primary/5" : "border-border/60 bg-white"}`}
                    >
                      <h3 className="font-display text-base font-semibold text-foreground">{p.name[lang]}</h3>
                      <div className="mt-2 font-display text-2xl font-bold text-primary">{p.price[lang]}</div>
                      <p className="mt-2 text-sm text-muted-foreground">{p.note[lang]}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {cert.modes.includes("digital") ? (
          <SamplingLocationsSection className="bg-muted/20 pb-12 lg:pb-16" />
        ) : null}

        {/* Related */}
        {related.length > 0 && (
          <section className="pb-16 lg:pb-20">
            <div className="container-wide">
              <h2 className="font-display text-2xl font-semibold text-foreground sm:text-3xl">{labels.relatedTitle}</h2>
              <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={certificatePathFor(r.slug, lang)}
                    aria-label={t.services.readMoreAbout.replace("{title}", r.title[lang])}
                    className="group flex flex-col rounded-2xl border border-border bg-card p-6 shadow-card transition-smooth hover:-translate-y-1 hover:border-primary/30 hover:shadow-elevated"
                  >
                    <CategoryBadge category={r.category} className="self-start" link />
                    <h3 className="mt-4 font-display text-lg font-semibold text-foreground">{r.title[lang]}</h3>
                    <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{r.description[lang]}</p>
                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary" aria-hidden>
                      {t.services.readMore}
                      <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* FAQ – always visible, cert-specific or generic fallback */}
        {(() => {
          const faqItems = details?.faq?.length
            ? details.faq.map((item) => ({ q: item.q[lang], a: item.a[lang] }))
            : labels.faqGeneric;
          return (
            <section className="pb-12 lg:pb-16">
              <div className="container-wide">
                  <h2 className="font-display text-2xl font-semibold text-foreground sm:text-3xl">{labels.faqTitle}</h2>
                  <div className="mt-6 space-y-3">
                    {faqItems.map((item, i) => (
                      <div key={i} className="overflow-hidden rounded-2xl border border-border/60 bg-white shadow-card sm:rounded-3xl">
                        <button
                          type="button"
                          className="flex w-full items-center justify-between gap-4 p-6 text-left"
                          onClick={() => setOpenFaq(openFaq === i ? null : i)}
                          aria-expanded={openFaq === i}
                        >
                          <span className="font-display text-base font-semibold text-foreground">{item.q}</span>
                          <ChevronDown
                            className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-200 ${openFaq === i ? "rotate-180" : ""}`}
                          />
                        </button>
                        {openFaq === i && (
                          <div className="px-6 pb-6 text-sm leading-relaxed text-muted-foreground">
                            {item.a}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
            </div>
          </section>
          );
        })()}

        <CTASection />
        <Breadcrumbs
          items={[
            { label: lang === "sv" ? "Hem" : "Home", to: lang === "sv" ? "/" : "/en" },
            { label: t.nav.certificates, to: lang === "sv" ? "/lakarintyg" : "/en/lakarintyg" },
            { label: cert.title[lang] },
          ]}
        />
      </main>
      <BookingDialog
        open={bookDialogOpen}
        onOpenChange={setBookDialogOpen}
        title={cert.title[lang]}
        action={selectedAction}
        slug={cert.slug}
        contentKey={cert.slug}
      />
      <Footer />
    </div>
  );
};

export default CertificateDetail;